import { useRef } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download, Network } from "lucide-react";
import { GraphCanvas } from "@/components/Graph/GraphCanvas";
import { exportGraphPng } from "@/components/Graph/exportPng";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getSharedGraph } from "@/lib/api";
import { useUIStore } from "@/stores/uiStore";

export function SharePage() {
  const { token } = useParams<{ token: string }>();
  const containerRef = useRef<HTMLDivElement>(null);
  const { graphLayout, setGraphLayout } = useUIStore();

  const { data: graph, isLoading, error } = useQuery({
    queryKey: ["shared-graph", token],
    queryFn: () => getSharedGraph(token!),
    enabled: !!token,
    retry: false,
  });

  function handleExportPng() {
    if (!containerRef.current) return;
    void exportGraphPng(containerRef.current, graph?.session?.name ?? "shared-session");
  }

  if (isLoading) {
    return (
      <div className="flex flex-col h-screen">
        <div className="p-4 border-b flex items-center gap-3">
          <Network className="h-5 w-5" />
          <Skeleton className="h-6 w-48" />
        </div>
        <Skeleton className="flex-1" />
      </div>
    );
  }

  if (error || !graph) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center space-y-3">
          <Network className="h-12 w-12 text-muted-foreground mx-auto" />
          <h1 className="text-xl font-semibold">Link expired or revoked</h1>
          <p className="text-muted-foreground">
            This share link is no longer valid.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
      {/* Minimal toolbar — read-only, no auth actions */}
      <div className="border-b bg-background px-4 py-2 flex items-center gap-3">
        <div className="flex items-center gap-2">
          <Network className="h-4 w-4 text-muted-foreground" />
          <span className="font-semibold">
            {graph.session?.name || "Shared session"}
          </span>
          <span className="text-xs text-muted-foreground">(read-only)</span>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <Tabs
            value={graphLayout}
            onValueChange={(v) =>
              setGraphLayout(v as "hierarchical" | "force")
            }
          >
            <TabsList className="h-8">
              <TabsTrigger value="hierarchical" className="text-xs h-7 px-2">
                Hierarchical
              </TabsTrigger>
              <TabsTrigger value="force" className="text-xs h-7 px-2">
                Force
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <Button size="sm" variant="outline" onClick={handleExportPng}>
            <Download className="h-3.5 w-3.5 mr-1" />
            Export PNG
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden relative" ref={containerRef}>
        <GraphCanvas graph={graph} className="w-full h-full" />
      </div>
    </div>
  );
}
